import { useMutation } from '@tanstack/react-query'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { AgentInstructionKind } from '../db/apiAgentInstructions'
import { ServerRoutes } from './serverRoutes'

export type InstructionDraft = {
  kind: AgentInstructionKind
  title: string
  body: string
}

export const useInstructionDraft = () => {
  const { session } = useAuth()
  const { toast } = useToast()
  const { t } = useLocalization()

  return useMutation({
    mutationFn: async ({
      chat_id,
      message_id,
      feedback,
    }: {
      chat_id: number
      message_id?: number | null
      feedback: string
    }): Promise<InstructionDraft> => {
      const token = session?.access_token
      if (!token) {
        throw new Error(t('You need to be signed in', 'Du måste vara inloggad'))
      }

      const response = await fetch(
        import.meta.env.VITE_BE_SERVER_URL +
          ServerRoutes.merchGPTInstructionDraft,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ chat_id, message_id, feedback }),
        }
      )

      const errorData = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(
          errorData.msg ||
            t('Failed to draft instruction', 'Kunde inte skapa utkast')
        )
      }

      return errorData as InstructionDraft
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('Could not draft instruction', 'Kunde inte skapa utkast'),
        description: error.message,
      })
    },
  })
}
